'use client'
import { ProjectDetailFragment } from '@/gql/generated/graphql'
import { CommonFilterDropdown, Option } from '@components/grid/filter-panel'
import {
  AttributesFilter,
  AttributeField,
  AttributeFilter,
  constructEmptyFilter,
  BooleanAttributeOptionsFilter,
  BooleanAttributeFilter,
  EnumAttributeFilter,
  EnumAttributeOptionsFilter,
  NumberAttributeFilter
} from '@utils/token-features'
import { BodyText, DetailText, LeftCaretIcon, SearchIcon, XIcon } from 'design-configuration/components'
import { Button } from '@components/button'
import { LayoutGroup, motion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import SwitchToggle from '@components/switch-toggle'
import { shortenWalletAddress } from '@utils/artists'

type ProjectTokensFilterProps = {
  project: ProjectDetailFragment
  tokenFilter: AttributesFilter
  featureFields: AttributeField[] | undefined
  featureFieldsCounts?: Record<string, Record<string, number>>
  setTokenFilter: (newAttributesFilter: AttributesFilter | null) => void
  searchInvocationValue: string
  handleSearchInvocationChange: React.ChangeEventHandler<HTMLInputElement>
  searchInvocationSavedValues: number[]
  handleSaveSearchInvocation: () => void
  handleRemoveSearchInvocation: (value: number) => void
  filterOpen: boolean
  tokenCount: number
  numActiveFilters: number
  abListingsOnly: boolean
  onABListingsOnlyChange: (abListingsOnly: boolean | 'indeterminate') => void
  onFilterClose: () => void
  walletAddressValue: string
  handleWalletAddressChange: React.ChangeEventHandler<HTMLInputElement>
  walletAddressSavedValues: string[]
  handleSaveWalletAddress: () => void
  handleRemoveWalletAddress: (value: string) => void
  clearWalletAddressValue: () => void
  ensAddresses: Record<string, string>
  myArtworksOnly: boolean
  handleMyArtworksOnlyChange: (value: boolean | 'indeterminate') => void
}

export const ProjectTokensFilter = ({
  tokenFilter,
  featureFields,
  featureFieldsCounts,
  setTokenFilter,
  searchInvocationValue,
  handleSearchInvocationChange,
  searchInvocationSavedValues,
  handleSaveSearchInvocation,
  handleRemoveSearchInvocation,
  abListingsOnly,
  onABListingsOnlyChange,
  walletAddressValue,
  handleWalletAddressChange,
  walletAddressSavedValues,
  handleSaveWalletAddress,
  handleRemoveWalletAddress,
  ensAddresses,
  myArtworksOnly,
  handleMyArtworksOnlyChange
}: ProjectTokensFilterProps): React.ReactNode => {
  const searchInputRef = useRef<HTMLInputElement>(null)
  const walletAddressInputRef = useRef<HTMLInputElement>(null)

  const noFeatures =
    !featureFields?.length || featureFields.every((feature) => feature.type === 'enum' && !feature.options?.length)
  return (
    <>
      <div className="mb-md">
        <div className="py-sm">
          <DetailText className="text-on-surface-variant font-medium">FILTER BY MARKETPLACE</DetailText>
        </div>
        <motion.div className="py-xs flex items-center justify-between">
          <BodyText>Art Blocks listings only</BodyText>
          <SwitchToggle leftLabel="" switchedRight={abListingsOnly} onSwitchChange={onABListingsOnlyChange} />
        </motion.div>
      </div>
      <motion.div layout="position" className="py-sm">
        <DetailText className="text-on-surface-variant font-medium">FILTER BY OWNER</DetailText>
      </motion.div>
      <motion.div className="py-xs mb-xs flex items-center justify-between">
        <BodyText>My artworks only</BodyText>
        <SwitchToggle leftLabel="" switchedRight={myArtworksOnly} onSwitchChange={handleMyArtworksOnlyChange} />
      </motion.div>
      <motion.button
        layout="position"
        className="border-outline-variant p-xs gap-2xs rounded-xs mb-md flex h-auto w-full flex-wrap items-center border"
        onClick={() => walletAddressInputRef.current?.focus()}
      >
        <SearchIcon className="w-sm h-sm fill-on-surface-variant shrink-0" />

        {walletAddressSavedValues.map((savedValue) => (
          <motion.button
            layout="position"
            key={savedValue}
            className="px-xs border-outline-variant gap-x-xs flex items-center rounded-full border"
            onClick={() => handleRemoveWalletAddress(savedValue)}
          >
            <DetailText>{ensAddresses[savedValue] || shortenWalletAddress(savedValue)}</DetailText>
            <XIcon className="w-xs h-xs fill-on-surface-variant" />
          </motion.button>
        ))}
        <BodyText
          className={`${walletAddressSavedValues.length ? `w-[50px]` : 'w-4/5'} grow-0 overflow-hidden text-wrap text-left`}
        >
          <input
            placeholder={walletAddressSavedValues.length ? '' : 'Enter ENS or addresses'}
            type="text"
            className="w-full bg-transparent focus-visible:outline-none"
            ref={walletAddressInputRef}
            onChange={handleWalletAddressChange}
            value={walletAddressValue}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                handleSaveWalletAddress()
              } else if (e.key === 'Backspace') {
                if (walletAddressValue === '') {
                  handleRemoveWalletAddress(walletAddressSavedValues[walletAddressSavedValues.length - 1])
                }
              }
            }}
          />
        </BodyText>
      </motion.button>
      <motion.div layout="position" className="py-sm">
        <DetailText className="text-on-surface-variant font-medium">FILTER BY ARTWORK NUMBERS</DetailText>
      </motion.div>
      <motion.button
        layout="position"
        className="border-outline-variant p-xs gap-2xs rounded-xs mb-md flex h-auto w-full flex-wrap items-center border"
        onClick={() => searchInputRef.current?.focus()}
      >
        <SearchIcon className="w-sm h-sm fill-on-surface-variant shrink-0" />

        {searchInvocationSavedValues.map((savedValue) => (
          <motion.button
            layout="position"
            key={savedValue}
            className="px-xs border-outline-variant gap-x-xs flex items-center rounded-full border"
            onClick={() => handleRemoveSearchInvocation(savedValue)}
          >
            <DetailText>{savedValue}</DetailText>
            <XIcon className="w-xs h-xs fill-on-surface-variant" />
          </motion.button>
        ))}
        <BodyText
          className={`${searchInvocationSavedValues.length ? `w-[50px]` : 'w-4/5'} grow-0 overflow-hidden text-wrap text-left`}
        >
          <input
            placeholder={searchInvocationSavedValues.length ? '' : 'Enter numbers'}
            type="number"
            className="bg-transparent focus-visible:outline-none"
            ref={searchInputRef}
            onChange={handleSearchInvocationChange}
            value={searchInvocationValue}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            min={0}
            onKeyUp={(e) => {
              if (e.code === 'Minus') {
                e.preventDefault()
              } else if (e.key === 'Enter') {
                handleSaveSearchInvocation()
              } else if (e.key === 'Backspace') {
                if (searchInvocationValue === '') {
                  handleRemoveSearchInvocation(searchInvocationSavedValues[searchInvocationSavedValues.length - 1])
                }
              }
            }}
          />
        </BodyText>
      </motion.button>
      {noFeatures ? null : (
        <>
          <motion.div layout="position">
            <DetailText className="text-on-surface-variant mt-sm font-medium">FILTER BY ARTWORK FEATURE</DetailText>
          </motion.div>
          <LayoutGroup>
            {featureFields?.map((feature) => (
              <FeatureDropdown
                key={feature.name}
                onChange={(filter) => {
                  setTokenFilter({ ...tokenFilter, [feature.name]: filter })
                }}
                featureField={feature}
                optionCounts={featureFieldsCounts?.[feature.name]}
                filter={tokenFilter[feature.name]}
              />
            ))}
          </LayoutGroup>
        </>
      )}
    </>
  )
}

const FeatureDropdown = ({
  key,
  featureField,
  optionCounts,
  filter,
  onChange
}: {
  key: string
  featureField: AttributeField
  optionCounts?: Record<string, number>
  filter: AttributeFilter
  onChange: (filters: AttributeFilter) => void
}) => {
  const [open, setOpen] = useState(false)
  const getOptionsOfFeature = (feature: AttributeField): Option[] | null => {
    if (feature.type === 'number') {
      return null
    }

    switch (feature.type) {
      case 'enum':
        return (
          feature.options?.map((option) => ({
            label: option,
            value: option
          })) ?? []
        )
      case 'boolean':
        return [
          { label: 'True', value: 'true' },
          { label: 'False', value: 'false' }
        ]
      default:
        return []
    }
  }
  const currFeatureFilter = filter ?? constructEmptyFilter(featureField)

  let selectedFeatureOptionsBubbleText: string | null = null
  if (currFeatureFilter.type !== 'number' && Object.keys(currFeatureFilter.options).length > 0) {
    selectedFeatureOptionsBubbleText = `${
      Object.keys(currFeatureFilter.options).filter(
        (option) => currFeatureFilter.options[option as keyof typeof currFeatureFilter.options]
      ).length
    }`
  } else if (
    currFeatureFilter.type === 'number' &&
    (currFeatureFilter.min !== currFeatureFilter.domain[0] || currFeatureFilter.max !== currFeatureFilter.domain[1])
  ) {
    selectedFeatureOptionsBubbleText = 'RANGE'
  } else {
    selectedFeatureOptionsBubbleText = null
  }

  const handleEnumFilterChange = (option: string, value: boolean) => {
    if (currFeatureFilter.type !== 'enum') return

    const updatedOptionsFilter: EnumAttributeOptionsFilter = {}
    updatedOptionsFilter[option] = value

    const updatedAttributeFilter: EnumAttributeFilter = {
      ...currFeatureFilter,
      options: { ...currFeatureFilter.options, ...updatedOptionsFilter }
    }

    onChange(updatedAttributeFilter)
  }

  const handleNumberFilterChange = (min: number, max: number) => {
    if (currFeatureFilter && currFeatureFilter.type === 'number') {
      onChange({ ...currFeatureFilter, min, max })
    }
  }

  const handleBooleanFilterChange = (option: string, value: boolean) => {
    if (currFeatureFilter.type !== 'boolean') return

    const updatedOptionsFilter: BooleanAttributeOptionsFilter = {}
    updatedOptionsFilter[option as 'true' | 'false'] = value

    const updatedAttributeFilter: BooleanAttributeFilter = {
      ...currFeatureFilter,
      options: { ...currFeatureFilter.options, ...updatedOptionsFilter }
    }

    onChange(updatedAttributeFilter)
  }

  if (currFeatureFilter.type === 'enum') {
    return (
      <CommonFilterDropdown
        name={featureField.name}
        optionCounts={optionCounts}
        options={getOptionsOfFeature(featureField) ?? []}
        selectedOptions={Object.keys(currFeatureFilter.options).filter(
          (option) => currFeatureFilter.options[option as keyof typeof currFeatureFilter.options]
        )}
        onChange={(option: string, selected: boolean) => handleEnumFilterChange(option, selected)}
      />
    )
  } else if (currFeatureFilter.type === 'boolean') {
    return (
      <CommonFilterDropdown
        name={featureField.name}
        optionCounts={optionCounts}
        options={getOptionsOfFeature(featureField) ?? []}
        selectedOptions={Object.keys(currFeatureFilter.options).filter(
          (option) => currFeatureFilter.options[option as 'true' | 'false']
        )}
        onChange={(option: string) =>
          handleBooleanFilterChange(option, !currFeatureFilter.options[option as 'true' | 'false'])
        }
      />
    )
  }

  return (
    <motion.div className="gap-y-xs py-md border-outline-variant flex w-full flex-col border-b" key={key} layout>
      <motion.button
        className="text-on-surface flex w-full items-center justify-between"
        onClick={() => setOpen(!open)}
        layout
      >
        <BodyText>{featureField.name}</BodyText>
        <div className="gap-x-xs flex items-center">
          {selectedFeatureOptionsBubbleText && selectedFeatureOptionsBubbleText !== '0' ? (
            <div className="border-outline-variant px-xs rounded-full border py-[2px]">
              <DetailText className="font-medium">{selectedFeatureOptionsBubbleText}</DetailText>
            </div>
          ) : null}
          <motion.div
            animate={{
              rotate: open ? 90 : 270
            }}
            transition={{ duration: 0.2 }}
          >
            <LeftCaretIcon className={`w-sm h-sm fill-on-surface`} />
          </motion.div>
        </div>
      </motion.button>
      {open ? <NumberFeatureField featureFilter={currFeatureFilter} handleChange={handleNumberFilterChange} /> : null}
    </motion.div>
  )
}

const NumberFeatureField = ({
  featureFilter,
  handleChange
}: {
  featureFilter: NumberAttributeFilter
  handleChange: (min: number, max: number) => void
}) => {
  const domainMin = featureFilter.domain[0] ?? 0
  const domainMax = featureFilter.domain[1] ?? 0
  const currentMin = featureFilter.min ?? domainMin
  const currentMax = featureFilter.max ?? domainMax

  const [min, setMin] = useState(currentMin)
  const [max, setMax] = useState(currentMax)
  const [error, setError] = useState('')

  useEffect(() => {
    setMin(currentMin)
    setMax(currentMax)
  }, [currentMin, currentMax])

  useEffect(() => {
    if (min && min < domainMin) {
      setError(`Minimum must be greater than ${domainMin}`)
    } else if (max && max > domainMax) {
      setError(`Maximum must be less than ${domainMax}`)
    } else if (min > max) {
      setError('Minimum must be less than maximum')
    } else {
      setError('')
    }
  }, [min, max, domainMin, domainMax])

  return (
    <motion.div
      layout="position"
      className="gap-y-sm mt-md flex w-full flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="gap-x-xs flex w-full items-center justify-between">
        <BodyText className="w-full">
          <input
            id="min"
            placeholder={featureFilter.domain[0]?.toString() ?? ''}
            type="number"
            className="border-outline-variant px-sm py-xs rounded-xs flex w-full grow items-center justify-center border bg-transparent focus-visible:outline-none"
            onChange={(event) => {
              const num = parseFloat(event.target.value) || ''
              setMin(num as number)
            }}
            value={min}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            min={domainMin}
            max={max}
            onKeyUp={(e) => {
              if (e.code === 'Minus') {
                e.preventDefault()
              }
            }}
          />
        </BodyText>
        <BodyText>to</BodyText>
        <BodyText className="flex w-full items-center justify-center">
          <input
            id="max"
            placeholder={featureFilter.domain[1]?.toString() ?? ''}
            type="number"
            className="border-outline-variant px-sm py-xs rounded-xs flex w-full grow items-center justify-center border bg-transparent focus-visible:outline-none"
            onChange={(event) => {
              const num = parseFloat(event.target.value) || ''
              setMax(num as number)
            }}
            value={max}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            min={min}
            max={domainMax}
            onKeyUp={(e) => {
              if (e.code === 'Minus') {
                e.preventDefault()
              }
            }}
          />
        </BodyText>
      </div>
      {error ? <DetailText className="text-error">{error}</DetailText> : null}
      <Button
        colorTheme={'secondary'}
        onClick={() => handleChange(min ? min : domainMin, max ? max : domainMax)}
        disabled={(max === currentMax && min === currentMin) || !!error}
        className="w-full"
      >
        Apply
      </Button>
    </motion.div>
  )
}
